/**
 * TimingGoalAnnotation - Timing coordination goal annotation panel
 *
 * Allows coaches to define timing coordination between two body landmarks,
 * with per-landmark event detection types (velocity, angle threshold, ground
 * contact, peak extension).
 *
 * Design: Precision Instrument — dual event cards connected by a visual
 * relationship indicator, with sport-specific presets for instant setup.
 */

import { useState, useCallback, useEffect, useMemo } from 'react';
import {
  ClockIcon,
  BoltIcon,
  ArrowsRightLeftIcon,
  ChevronDownIcon,
} from '@heroicons/react/24/outline';
import { cn } from '@/utils/cn';
import { Tooltip } from '@/components/ui';
import AnnotationPanel from './AnnotationPanel';
import {
  TimingMeasurementType,
  TimingRelationship,
  FUNCTIONAL_LANDMARK_GROUPS,
  VIRTUAL_LANDMARK_GROUPS,
} from '@/types/api';
import type {
  TimingConfig,
  LandmarkEventConfig,
  Phase,
} from '@/types/api';
import { getLandmarkLabel } from '@/utils/landmarkDetection';
import FunctionalLandmarkPicker from './FunctionalLandmarkPicker';
import { resolveEventConfig } from './timingGoalSentence';

// ─── Constants ──────────────────────────────────────────────────────────────

/**
 * Available joints for ANGLE_THRESHOLD event detection.
 * Functional entries (lead_/trail_) are listed first to encourage their use —
 * they resolve based on athlete handedness at evaluation time.
 */
const JOINT_OPTIONS = [
  // Functional (handedness-aware) — preferred
  { value: 'lead_elbow', label: 'Lead Elbow' },
  { value: 'trail_elbow', label: 'Trail Elbow' },
  { value: 'lead_shoulder', label: 'Lead Shoulder' },
  { value: 'trail_shoulder', label: 'Trail Shoulder' },
  { value: 'lead_hip', label: 'Lead Hip' },
  { value: 'trail_hip', label: 'Trail Hip' },
  { value: 'lead_knee', label: 'Lead Knee' },
  { value: 'trail_knee', label: 'Trail Knee' },
  { value: 'lead_ankle', label: 'Lead Ankle' },
  { value: 'trail_ankle', label: 'Trail Ankle' },
  // Anatomical (specific side)
  { value: 'left_elbow', label: 'Left Elbow' },
  { value: 'right_elbow', label: 'Right Elbow' },
  { value: 'left_shoulder', label: 'Left Shoulder' },
  { value: 'right_shoulder', label: 'Right Shoulder' },
  { value: 'left_hip', label: 'Left Hip' },
  { value: 'right_hip', label: 'Right Hip' },
  { value: 'left_knee', label: 'Left Knee' },
  { value: 'right_knee', label: 'Right Knee' },
  { value: 'left_ankle', label: 'Left Ankle' },
  { value: 'right_ankle', label: 'Right Ankle' },
] as const;

/** Coach-friendly labels for measurement types (no jargon) */
const EVENT_TYPE_OPTIONS: {
  value: TimingMeasurementType;
  label: string;
  description: string;
  tooltip: string;
  icon: string;
}[] = [
  {
    value: TimingMeasurementType.VELOCITY_ONSET,
    label: 'Starts moving',
    description: 'When this body part first begins moving',
    tooltip: 'Detects the first frame where the selected landmark\'s velocity exceeds the configured threshold. Best for measuring the initiation of a movement (e.g., when the hand first starts moving toward the target).',
    icon: '▶',
  },
  {
    value: TimingMeasurementType.PEAK_VELOCITY,
    label: 'Moving fastest',
    description: 'When this body part reaches peak speed',
    tooltip: 'Detects the frame where the selected landmark reaches its maximum velocity during the phase. Best for measuring the explosive peak of a movement (e.g., fastest point of a punch or kick).',
    icon: '⚡',
  },
  {
    value: TimingMeasurementType.ANGLE_THRESHOLD,
    label: 'Reaches angle',
    description: 'When a joint reaches a specific bend or extension',
    tooltip: 'Detects the frame where a selected joint angle crosses the configured threshold (above or below). Best for measuring when a limb reaches a specific position (e.g., elbow reaches 160° extension).',
    icon: '📐',
  },
  {
    value: TimingMeasurementType.GROUND_CONTACT,
    label: 'Touches ground',
    description: 'When this body part contacts the ground',
    tooltip: 'Detects the frame where the selected landmark\'s downward velocity drops to near-zero after a descent. Best for detecting foot strikes, heel landings, or any body part touching down (e.g., lead heel contacts the floor during a lunge).',
    icon: '⬇',
  },
  {
    value: TimingMeasurementType.OFF_THE_GROUND,
    label: 'Leaves ground',
    description: 'When this body part lifts off the ground',
    tooltip: 'Detects the frame where the selected landmark begins moving upward after being stationary on the ground. The inverse of "Touches ground." Best for detecting push-off, toe-off, or any liftoff event (e.g., trail heel lifts before the lunge drive).',
    icon: '⬆',
  },
  {
    value: TimingMeasurementType.PEAK_EXTENSION,
    label: 'Fully extended',
    description: 'When this body part reaches maximum reach and stops',
    tooltip: 'Detects the frame where the selected landmark reaches its farthest point from the body center and stops moving forward. Best for detecting full arm extension, maximum reach, or the end of a pushing motion.',
    icon: '↗',
  },
];

/** Relationship labels */
const RELATIONSHIP_OPTIONS: {
  value: TimingRelationship;
  label: string;
  shortLabel: string;
  description: string;
}[] = [
  {
    value: TimingRelationship.SEQUENTIAL_A_FIRST,
    label: 'A happens first',
    shortLabel: 'A → B',
    description: 'Event A must occur before Event B',
  },
  {
    value: TimingRelationship.SEQUENTIAL_B_FIRST,
    label: 'B happens first',
    shortLabel: 'B → A',
    description: 'Event B must occur before Event A',
  },
  {
    value: TimingRelationship.SIMULTANEOUS,
    label: 'At the same time',
    shortLabel: 'A ↔ B',
    description: 'Both events should occur simultaneously',
  },
];

// ─── Sentence Builder — option data ─────────────────────────────────────────

/**
 * Landmark option groups for the sentence-builder slot selects.
 * Sourced from the same FUNCTIONAL_LANDMARK_GROUPS + VIRTUAL_LANDMARK_GROUPS
 * constants used by FunctionalLandmarkPicker — data-driven, no hardcoded list.
 *
 * Anatomical variants (left_x/right_x) are derived from FUNCTIONAL_LANDMARK_GROUPS
 * for backward compat with configs that store left_/right_ landmark names.
 * This covers all joints including wrists (which JOINT_OPTIONS omits since that
 * constant is limited to angle-threshold joints).
 */
const _ANATOMICAL_LANDMARK_OPTIONS: { value: string; label: string }[] = (() => {
  // Extract unique base names from functional landmarks (e.g. 'wrist', 'knee', 'heel')
  const bases = new Set<string>();
  Object.values(FUNCTIONAL_LANDMARK_GROUPS).flat().forEach((name) => {
    bases.add(name.replace(/^(lead_|trail_)/, ''));
  });
  return Array.from(bases).flatMap((base) => [
    { value: `left_${base}`, label: getLandmarkLabel(`left_${base}`) },
    { value: `right_${base}`, label: getLandmarkLabel(`right_${base}`) },
  ]);
})();

const LANDMARK_OPT_GROUPS: { label: string; options: { value: string; label: string }[] }[] = [
  ...Object.entries(FUNCTIONAL_LANDMARK_GROUPS).map(([group, names]) => ({
    label: group,
    options: names.map((name) => ({ value: name, label: getLandmarkLabel(name) })),
  })),
  ...Object.entries(VIRTUAL_LANDMARK_GROUPS).map(([group, items]) => ({
    label: group,
    options: items.map(({ value, label }) => ({ value, label })),
  })),
  {
    label: 'Anatomical',
    options: _ANATOMICAL_LANDMARK_OPTIONS,
  },
];

/** Natural-language relationship labels for the sentence builder. */
const SENTENCE_RELATIONSHIP_OPTIONS: { value: TimingRelationship; label: string }[] = [
  { value: TimingRelationship.SEQUENTIAL_A_FIRST, label: 'BEFORE' },
  { value: TimingRelationship.SEQUENTIAL_B_FIRST, label: 'AFTER' },
  { value: TimingRelationship.SIMULTANEOUS, label: 'AT THE SAME TIME AS' },
];

// ─── Sport Presets ──────────────────────────────────────────────────────────

interface TimingPreset {
  id: string;
  label: string;
  sport: string;
  description: string;
  landmark_a: string;
  landmark_b: string;
  event_a: LandmarkEventConfig;
  event_b: LandmarkEventConfig;
  relationship: TimingRelationship;
  simultaneity_tolerance_frames?: number;
  min_lag_frames?: number;
  max_lag_frames?: number;
}

const TIMING_PRESETS: TimingPreset[] = [
  {
    id: 'jkd_punch_before_step',
    label: 'Punch before step',
    sport: 'JKD / Wing Chun',
    description: 'Fist reaches target before lead foot lands',
    landmark_a: 'lead_wrist',
    landmark_b: 'lead_heel',
    event_a: { measurement_type: TimingMeasurementType.PEAK_EXTENSION },
    event_b: { measurement_type: TimingMeasurementType.GROUND_CONTACT },
    relationship: TimingRelationship.SEQUENTIAL_A_FIRST,
    min_lag_frames: 1,
    max_lag_frames: 10,
  },
  {
    id: 'boxing_jab_with_step',
    label: 'Jab with step',
    sport: 'Boxing',
    description: 'Jab lands at same time as lead foot plants',
    landmark_a: 'lead_wrist',
    landmark_b: 'lead_heel',
    event_a: { measurement_type: TimingMeasurementType.PEAK_EXTENSION },
    event_b: { measurement_type: TimingMeasurementType.GROUND_CONTACT },
    relationship: TimingRelationship.SIMULTANEOUS,
    simultaneity_tolerance_frames: 3,
  },
  {
    id: 'fencing_extension_before_contact',
    label: 'Extend before lunge lands',
    sport: 'Fencing',
    description: 'Rear leg fully extends before lead heel contacts ground',
    landmark_a: 'trail_knee',
    landmark_b: 'lead_heel',
    event_a: {
      measurement_type: TimingMeasurementType.ANGLE_THRESHOLD,
      angle_joint_name: 'trail_knee',
      angle_threshold_degrees: 160,
      angle_crossing_direction: 'above',
    },
    event_b: { measurement_type: TimingMeasurementType.GROUND_CONTACT },
    relationship: TimingRelationship.SEQUENTIAL_A_FIRST,
  },
  {
    id: 'hip_before_shoulder',
    label: 'Hips lead shoulders',
    sport: 'General',
    description: 'Hip rotation initiates before shoulder rotation',
    landmark_a: 'lead_hip',
    landmark_b: 'lead_shoulder',
    event_a: { measurement_type: TimingMeasurementType.VELOCITY_ONSET },
    event_b: { measurement_type: TimingMeasurementType.VELOCITY_ONSET },
    relationship: TimingRelationship.SEQUENTIAL_A_FIRST,
  },
  // ── Kinetic-Chain Presets ────────────────────────────────────────────────
  // These use the onset→peak pairing: VELOCITY_ONSET on the proximal landmark
  // fires earlier (better detection resolution) while PEAK_VELOCITY on the
  // distal landmark marks the explosive endpoint. This pairing is 100% reliable
  // for ordering but the displayed ms lag carries a structural ~150–265 ms offset
  // (onset→peak structural difference) so it should NOT be read as the true
  // physiological inter-segment lead time.
  // Require frontal or diagonal camera — from a side view, hip velocity measures
  // forward drive, not axial rotation (measurement validity issue).
  {
    id: 'boxing_cross_core_first',
    label: 'Core before arm (cross)',
    sport: 'Boxing / Striking',
    description: 'Hip rotation initiates before the lead hand reaches peak speed. Use a frontal or diagonal camera; side view measures forward drive, not rotation.',
    landmark_a: 'lead_hip',
    landmark_b: 'lead_wrist',
    event_a: { measurement_type: TimingMeasurementType.VELOCITY_ONSET },
    event_b: { measurement_type: TimingMeasurementType.PEAK_VELOCITY },
    relationship: TimingRelationship.SEQUENTIAL_A_FIRST,
  },
  {
    id: 'tennis_forehand_hip_first',
    label: 'Hip leads shoulder (forehand) — step 1 of 2',
    sport: 'Tennis',
    description: 'Hip rotation starts before the shoulder reaches peak speed. This is the FIRST of two goals for a full kinetic chain: add a second goal with lead_shoulder → lead_wrist to complete the chain.',
    landmark_a: 'lead_hip',
    landmark_b: 'lead_shoulder',
    event_a: { measurement_type: TimingMeasurementType.VELOCITY_ONSET },
    event_b: { measurement_type: TimingMeasurementType.PEAK_VELOCITY },
    relationship: TimingRelationship.SEQUENTIAL_A_FIRST,
  },
  {
    id: 'simultaneous_arm_swing',
    label: 'Arms move together',
    sport: 'General',
    description: 'Both arms begin moving at the same time',
    landmark_a: 'lead_wrist',
    landmark_b: 'trail_wrist',
    event_a: { measurement_type: TimingMeasurementType.VELOCITY_ONSET },
    event_b: { measurement_type: TimingMeasurementType.VELOCITY_ONSET },
    relationship: TimingRelationship.SIMULTANEOUS,
    simultaneity_tolerance_frames: 5,
  },
];

// ─── Defaults ───────────────────────────────────────────────────────────────

const defaultTimingConfig: TimingConfig = {
  enabled: true,
  landmark_a: '',
  landmark_b: '',
  measurement_type: TimingMeasurementType.VELOCITY_ONSET,
  velocity_threshold: 0.02,
  relationship: TimingRelationship.SEQUENTIAL_A_FIRST,
  simultaneity_tolerance_frames: 3,
  use_template_reference: true,
  template_tolerance_percent: 30.0,
};

// ─── Helpers ────────────────────────────────────────────────────────────────

/** Check if an event type needs the angle config sub-panel */
function needsAngleConfig(type: TimingMeasurementType): boolean {
  return type === TimingMeasurementType.ANGLE_THRESHOLD;
}

/** Auto-generate label from config state */
function generateLabel(config: TimingConfig): string {
  if (!config.landmark_a || !config.landmark_b) return '';
  const a = getLandmarkLabel(config.landmark_a);
  const b = getLandmarkLabel(config.landmark_b);
  const rel = RELATIONSHIP_OPTIONS.find((r) => r.value === config.relationship);
  if (config.relationship === TimingRelationship.SIMULTANEOUS) {
    return `Timing: ${a} ↔ ${b}`;
  }
  return `Timing: ${a} → ${b} (${rel?.label || ''})`;
}

// ─── Sub-Components ─────────────────────────────────────────────────────────

/** Compact event type selector grid */
function EventTypeSelector({
  value,
  onChange,
}: {
  value: TimingMeasurementType;
  onChange: (type: TimingMeasurementType) => void;
}) {
  return (
    <div className="space-y-1.5">
      <label className="block text-[11px] font-semibold text-text-secondary uppercase tracking-wider">
        Detect when it…
      </label>
      <div className="grid grid-cols-1 gap-1">
        {EVENT_TYPE_OPTIONS.map((opt) => (
          <Tooltip
            key={opt.value}
            content={
              <div className="max-w-xs text-xs leading-relaxed">
                <p className="font-semibold text-text-primary mb-1">{opt.icon} {opt.label}</p>
                <p>{opt.tooltip}</p>
              </div>
            }
            position="right"
          >
            <button
              type="button"
              onClick={() => onChange(opt.value)}
              className={cn(
                'flex items-center gap-2.5 px-3 py-2 rounded-lg text-left transition-all text-sm w-full',
                'border',
                value === opt.value
                  ? 'bg-cyan-500/15 border-cyan-500/50 text-cyan-300'
                  : 'bg-dark-elevated-1 border-transparent text-text-secondary hover:bg-dark-elevated-2 hover:text-text-primary'
              )}
            >
              <span className="text-base leading-none w-5 text-center flex-shrink-0">{opt.icon}</span>
              <div className="min-w-0">
                <div className="font-medium text-[13px] leading-tight">{opt.label}</div>
                <div className="text-[11px] text-text-muted leading-tight mt-0.5 line-clamp-1">
                  {opt.description}
                </div>
              </div>
            </button>
          </Tooltip>
        ))}
      </div>
    </div>
  );
}

/** Angle threshold configuration sub-panel */
function AngleThresholdConfig({
  eventConfig,
  onChange,
}: {
  eventConfig: LandmarkEventConfig;
  onChange: (config: LandmarkEventConfig) => void;
}) {
  const jointName = eventConfig.angle_joint_name || JOINT_OPTIONS[0].value;
  const threshold = eventConfig.angle_threshold_degrees ?? 160;
  const direction = eventConfig.angle_crossing_direction || 'above';

  return (
    <div className="mt-2 space-y-3 p-3 rounded-lg bg-dark-elevated-1/50 border border-cyan-500/20">
      {/* Joint selector */}
      <div>
        <label className="block text-[11px] font-semibold text-text-secondary uppercase tracking-wider mb-1.5">
          Joint
        </label>
        <select
          value={jointName}
          onChange={(e) =>
            onChange({
              ...eventConfig,
              angle_joint_name: e.target.value,
            })
          }
          className="w-full px-3 py-2 rounded-lg border border-border-subtle bg-dark-elevated-1 text-text-primary text-sm focus:outline-none focus:border-cyan-500 focus:ring-2 ring-cyan-500/20 transition-all"
        >
          {JOINT_OPTIONS.map((j) => (
            <option key={j.value} value={j.value}>
              {j.label}
            </option>
          ))}
        </select>
      </div>

      {/* Angle threshold */}
      <div>
        <div className="flex items-center justify-between mb-1.5">
          <label className="text-[11px] font-semibold text-text-secondary uppercase tracking-wider">
            Angle
          </label>
          <span className="text-xs font-semibold text-cyan-400 tabular-nums">
            {threshold}°
          </span>
        </div>
        <input
          type="range"
          min="30"
          max="180"
          step="5"
          value={threshold}
          onChange={(e) =>
            onChange({
              ...eventConfig,
              angle_threshold_degrees: parseInt(e.target.value),
            })
          }
          className="w-full h-1.5 bg-dark-elevated-2 rounded-lg appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-cyan-500 [&::-webkit-slider-thumb]:cursor-pointer [&::-webkit-slider-thumb]:shadow-md"
        />
        <div className="flex justify-between text-[10px] text-text-muted mt-0.5">
          <span>30° (bent)</span>
          <span>180° (straight)</span>
        </div>
      </div>

      {/* Crossing direction */}
      <div>
        <label className="block text-[11px] font-semibold text-text-secondary uppercase tracking-wider mb-1.5">
          Trigger when angle goes…
        </label>
        <div className="grid grid-cols-2 gap-1.5">
          {[
            { value: 'above', label: 'Above', desc: 'Extending' },
            { value: 'below', label: 'Below', desc: 'Bending' },
          ].map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() =>
                onChange({
                  ...eventConfig,
                  angle_crossing_direction: opt.value as 'above' | 'below',
                })
              }
              className={cn(
                'px-3 py-2 rounded-lg border text-sm transition-all text-center',
                direction === opt.value
                  ? 'bg-cyan-500/15 border-cyan-500/50 text-cyan-300'
                  : 'bg-dark-elevated-1 border-border-subtle text-text-secondary hover:text-text-primary'
              )}
            >
              <div className="font-medium">{opt.label} {threshold}°</div>
              <div className="text-[10px] text-text-muted">{opt.desc}</div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

/** Single event card for one landmark in the pair */
function EventCard({
  side,
  landmark,
  eventConfig,
  onSelectLandmark,
  onLandmarkChange,
  onEventConfigChange,
  canSelectLandmark,
}: {
  side: 'A' | 'B';
  landmark: string;
  eventConfig: LandmarkEventConfig;
  /** Called when skeleton-click mode selects a landmark (triggers parent callback) */
  onSelectLandmark: () => void;
  /** Called when a landmark is chosen (functional chip or skeleton click result) */
  onLandmarkChange: (name: string) => void;
  onEventConfigChange: (config: LandmarkEventConfig) => void;
  canSelectLandmark: boolean;
}) {
  return (
    <div className="rounded-xl border border-border-subtle bg-dark-elevated-1/30 overflow-hidden">
      {/* Card header */}
      <div className="flex items-center gap-2 px-3.5 py-2.5 bg-dark-elevated-2/50 border-b border-border-subtle">
        <div
          className={cn(
            'w-6 h-6 rounded-md flex items-center justify-center text-xs font-bold',
            side === 'A'
              ? 'bg-cyan-500/20 text-cyan-400'
              : 'bg-amber-500/20 text-amber-400'
          )}
        >
          {side}
        </div>
        <span className="text-xs font-semibold text-text-secondary uppercase tracking-wider">
          Event {side}
        </span>
      </div>

      <div className="p-3.5 space-y-3">
        {/* Landmark selector */}
        <div>
          <label className="block text-[11px] font-semibold text-text-secondary uppercase tracking-wider mb-1.5">
            Body Part
          </label>
          <FunctionalLandmarkPicker
            value={landmark}
            onChange={onLandmarkChange}
            onClickSkeleton={onSelectLandmark}
            canClickSkeleton={canSelectLandmark}
            side={side}
          />
        </div>

        {/* Event type selector */}
        <EventTypeSelector
          value={eventConfig.measurement_type}
          onChange={(type) => {
            const updated: LandmarkEventConfig = {
              ...eventConfig,
              measurement_type: type,
            };
            // Set defaults for angle_threshold
            if (type === TimingMeasurementType.ANGLE_THRESHOLD && !updated.angle_joint_name) {
              updated.angle_joint_name = JOINT_OPTIONS[0].value;
              updated.angle_threshold_degrees = 160;
              updated.angle_crossing_direction = 'above';
            }
            onEventConfigChange(updated);
          }}
        />

        {/* Conditional: angle threshold config */}
        {needsAngleConfig(eventConfig.measurement_type) && (
          <AngleThresholdConfig
            eventConfig={eventConfig}
            onChange={onEventConfigChange}
          />
        )}

        {/* Contextual hint for ground_contact */}
        {eventConfig.measurement_type === TimingMeasurementType.GROUND_CONTACT && (
          <div className="px-3 py-2 rounded-lg bg-emerald-500/10 border border-emerald-500/20 text-[11px] text-emerald-400">
            Auto-detected from vertical motion — no extra config needed
          </div>
        )}

        {/* Contextual hint for off_the_ground */}
        {eventConfig.measurement_type === TimingMeasurementType.OFF_THE_GROUND && (
          <div className="px-3 py-2 rounded-lg bg-amber-500/10 border border-amber-500/20 text-[11px] text-amber-400">
            Detects upward motion after being stationary on the ground — no extra config needed
          </div>
        )}

        {/* Contextual hint for peak_extension */}
        {eventConfig.measurement_type === TimingMeasurementType.PEAK_EXTENSION && (
          <div className="px-3 py-2 rounded-lg bg-violet-500/10 border border-violet-500/20 text-[11px] text-violet-400">
            Detects when this body part stops moving forward (full reach)
          </div>
        )}
      </div>
    </div>
  );
}

/** Visual relationship connector between the two event cards */
function RelationshipConnector({
  relationship,
  onChange,
}: {
  relationship: TimingRelationship;
  onChange: (rel: TimingRelationship) => void;
}) {
  const current = RELATIONSHIP_OPTIONS.find((r) => r.value === relationship);

  return (
    <div className="flex flex-col items-center gap-1 py-1.5">
      {/* Connecting line top */}
      <div className="w-px h-3 bg-gradient-to-b from-cyan-500/40 to-border-subtle" />

      {/* Relationship selector */}
      <div className="relative w-full">
        <div className="flex items-center justify-center gap-1.5">
          {RELATIONSHIP_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => onChange(opt.value)}
              title={opt.description}
              className={cn(
                'px-3 py-1.5 rounded-full text-xs font-semibold transition-all',
                'border',
                relationship === opt.value
                  ? 'bg-cyan-500/20 border-cyan-500/50 text-cyan-300 shadow-sm shadow-cyan-500/10'
                  : 'bg-dark-elevated-1 border-border-subtle text-text-muted hover:text-text-secondary hover:bg-dark-elevated-2'
              )}
            >
              {opt.shortLabel}
            </button>
          ))}
        </div>
        {current && (
          <p className="text-center text-[10px] text-text-muted mt-1.5">
            {current.description}
          </p>
        )}
      </div>

      {/* Connecting line bottom */}
      <div className="w-px h-3 bg-gradient-to-b from-border-subtle to-amber-500/40" />
    </div>
  );
}

// ─── Sentence Builder Components ─────────────────────────────────────────────

/**
 * Styled inline <select> for one slot in the sentence builder.
 * Renders differently depending on whether a value is selected and which
 * slot it represents (landmark A/B, event A/B, relationship).
 */
function SentenceSlot({
  testId,
  value,
  onChange,
  placeholder,
  accent,
  children,
}: {
  testId: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  /** 'a' = cyan (landmark/event A), 'b' = amber (landmark/event B), 'rel' = relationship */
  accent?: 'a' | 'b' | 'rel';
  children: React.ReactNode;
}) {
  const hasValue = !!value;
  return (
    <select
      data-testid={testId}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className={cn(
        'max-w-[11rem] px-2 py-1 rounded-lg border text-[13px] font-medium',
        'focus:outline-none focus:ring-2 focus:ring-cyan-500/20 cursor-pointer transition-colors',
        'bg-dark-elevated-2',
        hasValue
          ? accent === 'b'
            ? 'border-amber-500/30 text-amber-200 hover:border-amber-500/50'
            : accent === 'rel'
              ? 'border-cyan-500/50 text-cyan-300 font-semibold hover:border-cyan-500/70'
              : 'border-cyan-500/30 text-cyan-200 hover:border-cyan-500/50'
          : 'border-dashed border-border-subtle text-text-muted hover:border-cyan-500/30 italic',
      )}
    >
      {placeholder && <option value="">{placeholder}</option>}
      {children}
    </select>
  );
}

/**
 * SentenceBuilder — the primary timing-goal authoring interface.
 *
 * Renders five inline slot-pickers that together form a plain-language
 * coaching rule:
 *   [Landmark A] [Event A] BEFORE/AFTER/SIMULTANEOUS-WITH [Landmark B] [Event B]
 *
 * This is a UI-representation layer only. The underlying TimingConfig JSON
 * written by these slots is identical to what the event-card form in the
 * Advanced section produces for equivalent selections.
 *
 * Landmark options are sourced from FUNCTIONAL_LANDMARK_GROUPS (same data
 * used by FunctionalLandmarkPicker) — data-driven, not hardcoded.
 * Anatomical (left_/right_) options are appended for backward compat.
 */
function SentenceBuilder({
  config,
  updateConfig,
  eventA,
  eventB,
}: {
  config: TimingConfig;
  updateConfig: (patch: Partial<TimingConfig>) => void;
  eventA: LandmarkEventConfig;
  eventB: LandmarkEventConfig;
}) {
  const needsAngle =
    eventA.measurement_type === TimingMeasurementType.ANGLE_THRESHOLD ||
    eventB.measurement_type === TimingMeasurementType.ANGLE_THRESHOLD;

  /** Apply angle defaults when switching TO angle_threshold (matches EventTypeSelector logic) */
  function buildEventWithDefaults(
    base: LandmarkEventConfig,
    newType: TimingMeasurementType,
  ): LandmarkEventConfig {
    const updated: LandmarkEventConfig = { ...base, measurement_type: newType };
    if (newType === TimingMeasurementType.ANGLE_THRESHOLD && !updated.angle_joint_name) {
      updated.angle_joint_name = JOINT_OPTIONS[0].value as string;
      updated.angle_threshold_degrees = 160;
      updated.angle_crossing_direction = 'above';
    }
    return updated;
  }

  return (
    <div className="space-y-2">
      <label className="block text-[11px] font-semibold text-text-secondary uppercase tracking-wider">
        Timing Rule
      </label>

      {/* ── Five inline slots ───────────────────────────────────────── */}
      <div className="flex flex-wrap items-center gap-1.5">
        {/* Slot 1: Landmark A */}
        <SentenceSlot
          testId="sentence-slot-landmark-a"
          value={config.landmark_a}
          onChange={(v) => updateConfig({ landmark_a: v })}
          placeholder="body part A"
          accent="a"
        >
          {LANDMARK_OPT_GROUPS.map(({ label: groupLabel, options }) => (
            <optgroup key={groupLabel} label={groupLabel}>
              {options.map(({ value, label }) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </optgroup>
          ))}
        </SentenceSlot>

        {/* Slot 2: Event A */}
        <SentenceSlot
          testId="sentence-slot-event-a"
          value={eventA.measurement_type}
          onChange={(v) =>
            updateConfig({ event_a: buildEventWithDefaults(eventA, v as TimingMeasurementType) })
          }
          accent="a"
        >
          {EVENT_TYPE_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </SentenceSlot>

        {/* Slot 3: Relationship */}
        <SentenceSlot
          testId="sentence-slot-relationship"
          value={config.relationship}
          onChange={(v) => updateConfig({ relationship: v as TimingRelationship })}
          accent="rel"
        >
          {SENTENCE_RELATIONSHIP_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </SentenceSlot>

        {/* Slot 4: Landmark B */}
        <SentenceSlot
          testId="sentence-slot-landmark-b"
          value={config.landmark_b}
          onChange={(v) => updateConfig({ landmark_b: v })}
          placeholder="body part B"
          accent="b"
        >
          {LANDMARK_OPT_GROUPS.map(({ label: groupLabel, options }) => (
            <optgroup key={groupLabel} label={groupLabel}>
              {options.map(({ value, label }) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </optgroup>
          ))}
        </SentenceSlot>

        {/* Slot 5: Event B */}
        <SentenceSlot
          testId="sentence-slot-event-b"
          value={eventB.measurement_type}
          onChange={(v) =>
            updateConfig({ event_b: buildEventWithDefaults(eventB, v as TimingMeasurementType) })
          }
          accent="b"
        >
          {EVENT_TYPE_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </SentenceSlot>
      </div>

      {/* Angle-threshold hint — configure details in Advanced */}
      {needsAngle && (
        <p className="text-[11px] text-amber-400/80">
          Angle details (joint, threshold, direction) are configured in the Advanced section below.
        </p>
      )}
    </div>
  );
}

// ─── Kinetic-chain detection ─────────────────────────────────────────────────

/**
 * IDs of presets that encode kinetic-chain / sequencing goals.
 * Kept in sync with TIMING_PRESETS above.
 */
const KINETIC_CHAIN_PRESET_IDS = new Set([
  'boxing_cross_core_first',
  'tennis_forehand_hip_first',
]);

/**
 * The distal endpoints that, when paired with a lead_hip proximal landmark
 * using an onset→peak event combo, indicate a kinetic-chain sequencing goal.
 * Used as a heuristic when no preset was explicitly applied.
 */
const KINETIC_CHAIN_DISTAL_LANDMARKS = new Set([
  'lead_wrist', 'trail_wrist', 'left_wrist', 'right_wrist',
  'lead_shoulder', 'trail_shoulder', 'left_shoulder', 'right_shoulder',
  'lead_elbow', 'trail_elbow', 'left_elbow', 'right_elbow',
]);

const KINETIC_CHAIN_PROXIMAL_LANDMARKS = new Set([
  'lead_hip', 'trail_hip', 'left_hip', 'right_hip',
]);

/**
 * Returns true when the current config looks like a kinetic-chain sequencing
 * goal. Checks:
 * 1. The active preset is one of the dedicated kinetic-chain presets.
 * 2. OR landmark_a is a hip and landmark_b is a wrist/shoulder/elbow,
 *    with an onset/peak event combo and SEQUENTIAL_A_FIRST relationship.
 *
 * Advisory only — never used to block authoring.
 */
function isKineticChainGoal(
  config: TimingConfig,
  activePresetId: string | null,
): boolean {
  if (activePresetId && KINETIC_CHAIN_PRESET_IDS.has(activePresetId)) {
    return true;
  }
  if (
    KINETIC_CHAIN_PROXIMAL_LANDMARKS.has(config.landmark_a) &&
    KINETIC_CHAIN_DISTAL_LANDMARKS.has(config.landmark_b) &&
    config.relationship === TimingRelationship.SEQUENTIAL_A_FIRST
  ) {
    const eventAType = config.event_a?.measurement_type ?? config.measurement_type;
    const eventBType = config.event_b?.measurement_type ?? config.measurement_type;
    const isOnsetPeak =
      (eventAType === TimingMeasurementType.VELOCITY_ONSET ||
        eventBType === TimingMeasurementType.PEAK_VELOCITY);
    return isOnsetPeak;
  }
  return false;
}

// ─── Main Component ─────────────────────────────────────────────────────────

interface TimingGoalAnnotationProps {
  // Panel state
  isExpanded: boolean;
  onToggle: () => void;
  panelNumber?: number;
  totalPanels?: number;

  // Timing goal state
  timingConfig: TimingConfig | undefined;
  onTimingConfigChange: (config: TimingConfig | undefined) => void;

  // Phase context
  phases?: Phase[];
  currentPhase?: Phase | null;

  // Save callback
  onSave: (config: TimingConfig, label: string, notes: string, importance: number) => Promise<void>;

  // Landmark selection callbacks
  onRequestLandmarkASelection?: (callback: (landmark: string) => void) => void;
  onRequestLandmarkBSelection?: (callback: (landmark: string) => void) => void;

  // Status
  isSaved?: boolean;
  isSelectionActive?: boolean;

  /**
   * Camera view of the template being authored.
   * When 'side' and the current goal is a kinetic-chain goal, an advisory
   * banner is shown. The banner is non-blocking — authoring is always allowed.
   * If undefined (not provided), no banner is shown regardless of config.
   */
  cameraView?: string;

  className?: string;
}

export default function TimingGoalAnnotation({
  isExpanded,
  onToggle,
  panelNumber = 4,
  totalPanels = 4,
  timingConfig,
  onTimingConfigChange,
  phases,
  currentPhase,
  onSave,
  onRequestLandmarkASelection,
  onRequestLandmarkBSelection,
  isSaved = false,
  isSelectionActive = false,
  cameraView,
  className,
}: TimingGoalAnnotationProps) {
  const [label, setLabel] = useState('');
  const [notes, setNotes] = useState('');
  const [importance, setImportance] = useState(50);
  const [isSaving, setIsSaving] = useState(false);
  const [localIsSaved, setLocalIsSaved] = useState(false);
  const [showPresets, setShowPresets] = useState(false);
  const [showPhaseWarning, setShowPhaseWarning] = useState(false);
  // Tracks which preset was last applied — used for kinetic-chain detection heuristic
  const [activePresetId, setActivePresetId] = useState<string | null>(null);
  // Advanced section (event cards + timing constraints + template reference) is
  // collapsed by default so the sentence summary is the primary view.
  const [showAdvanced, setShowAdvanced] = useState(false);

  const config = timingConfig || defaultTimingConfig;

  // Resolve per-landmark event configs
  const eventA = useMemo(() => resolveEventConfig(config, 'a'), [config]);
  const eventB = useMemo(() => resolveEventConfig(config, 'b'), [config]);

  // Auto-generate label
  useEffect(() => {
    const autoLabel = generateLabel(config);
    if (!autoLabel) return;
    const isAutoGenerated =
      !label || label.includes('↔') || label.includes('Timing:') || label.includes('→');
    if (isAutoGenerated) {
      setLabel(autoLabel);
    }
  }, [config, config.landmark_a, config.landmark_b, config.relationship, label]);

  // ── Handlers ────────────────────────────────────────────────────────────

  const updateConfig = useCallback(
    (patch: Partial<TimingConfig>) => {
      onTimingConfigChange({ ...config, ...patch });
    },
    [config, onTimingConfigChange]
  );

  const handleEventAChange = useCallback(
    (eventConfig: LandmarkEventConfig) => {
      updateConfig({ event_a: eventConfig });
    },
    [updateConfig]
  );

  const handleEventBChange = useCallback(
    (eventConfig: LandmarkEventConfig) => {
      updateConfig({ event_b: eventConfig });
    },
    [updateConfig]
  );

  const handleSelectLandmarkA = useCallback(() => {
    if (onRequestLandmarkASelection) {
      onRequestLandmarkASelection((landmark: string) => {
        updateConfig({ landmark_a: landmark });
      });
    }
  }, [onRequestLandmarkASelection, updateConfig]);

  const handleSelectLandmarkB = useCallback(() => {
    if (onRequestLandmarkBSelection) {
      onRequestLandmarkBSelection((landmark: string) => {
        updateConfig({ landmark_b: landmark });
      });
    }
  }, [onRequestLandmarkBSelection, updateConfig]);

  const handleApplyPreset = useCallback(
    (preset: TimingPreset) => {
      onTimingConfigChange({
        ...config,
        landmark_a: preset.landmark_a,
        landmark_b: preset.landmark_b,
        event_a: preset.event_a,
        event_b: preset.event_b,
        relationship: preset.relationship,
        simultaneity_tolerance_frames:
          preset.simultaneity_tolerance_frames ?? config.simultaneity_tolerance_frames,
        min_lag_frames: preset.min_lag_frames,
        max_lag_frames: preset.max_lag_frames,
      });
      setActivePresetId(preset.id);
      setShowPresets(false);
    },
    [config, onTimingConfigChange]
  );

  const handleSave = useCallback(async () => {
    if (!config.landmark_a || !config.landmark_b || !label.trim()) return;

    // Warn if saving without a phase assignment on a template that has phases
    const hasPhases = phases && phases.length > 0;
    if (hasPhases && !currentPhase) {
      setShowPhaseWarning(true);
    }

    setIsSaving(true);
    try {
      await onSave(config, label.trim(), notes.trim(), importance);
      setLabel('');
      setNotes('');
      setImportance(50);
      setLocalIsSaved(true);
      setShowPhaseWarning(false);
      onTimingConfigChange(undefined);
      setTimeout(() => setLocalIsSaved(false), 3000);
    } catch (error) {
      console.error('Failed to save timing goal annotation:', error);
    } finally {
      setIsSaving(false);
    }
  }, [config, label, notes, importance, phases, currentPhase, onSave, onTimingConfigChange]);

  // ── Derived state ───────────────────────────────────────────────────────

  const hasCompleteTimingGoal = !!(config.enabled && config.landmark_a && config.landmark_b);
  const canSave = hasCompleteTimingGoal && !!label.trim();

  // Camera-view advisory banner: shown when a kinetic-chain goal is configured
  // AND the template uses a side-view camera. Advisory only — never prevents authoring.
  const showSideViewBanner =
    cameraView === 'side' && isKineticChainGoal(config, activePresetId);

  // ── Render ──────────────────────────────────────────────────────────────

  return (
    <AnnotationPanel
      title="Timing Goal"
      subtitle="Body part coordination"
      icon={<ClockIcon className="w-5 h-5" />}
      accentColor="blue"
      panelNumber={panelNumber}
      totalPanels={totalPanels}
      isExpanded={isExpanded}
      onToggle={onToggle}
      label={label}
      onLabelChange={setLabel}
      notes={notes}
      onNotesChange={setNotes}
      importance={importance}
      onImportanceChange={setImportance}
      canSave={canSave}
      isSaving={isSaving}
      isSaved={localIsSaved || isSaved}
      onSave={handleSave}
      saveButtonText="Save Timing Goal"
      className={className}
    >
      {/* Overlay when another goal type is selecting landmarks */}
      {isSelectionActive && (
        <div className="text-xs text-text-tertiary bg-dark-elevated-1/80 rounded-lg px-3 py-2 mb-3 text-center">
          Complete or cancel the active landmark selection to configure timing goals.
        </div>
      )}
      <div className={cn(isSelectionActive && 'opacity-50 pointer-events-none')}>
      {/* ── Camera-View Advisory Banner ─────────────────────────────────── */}
      {/* Advisory only — authoring is fully allowed from any camera view.    */}
      {/* Shown when a kinetic-chain goal is active and camera_view is 'side'. */}
      {showSideViewBanner && (
        <div
          role="note"
          aria-label="Side-view camera advisory"
          className="mb-3 flex items-start gap-2.5 rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2.5"
        >
          <span className="mt-0.5 text-amber-400 flex-shrink-0 text-base leading-none" aria-hidden="true">
            ⚠
          </span>
          <p className="text-[11px] text-amber-300 leading-relaxed">
            <span className="font-semibold">Side view detected.</span>{' '}
            Core-rotation timing goals work best with a frontal or diagonal
            camera. From a side view, hip velocity measures forward drive,
            not axial rotation. You can still author this goal — just be
            aware the measurement may not capture the intended rotation.
          </p>
        </div>
      )}

      {/* ── Presets Bar ─────────────────────────────────────────────────── */}
      <div className="mb-4">
        <button
          type="button"
          onClick={() => setShowPresets(!showPresets)}
          className={cn(
            'w-full flex items-center justify-between px-3 py-2 rounded-lg border transition-all text-sm',
            showPresets
              ? 'bg-cyan-500/10 border-cyan-500/30 text-cyan-300'
              : 'bg-dark-elevated-1 border-border-subtle text-text-secondary hover:bg-dark-elevated-2 hover:text-text-primary'
          )}
        >
          <div className="flex items-center gap-2">
            <BoltIcon className="w-4 h-4" />
            <span className="font-medium">Quick Presets</span>
          </div>
          <span className="text-[10px] text-text-muted">
            {showPresets ? 'Hide' : `${TIMING_PRESETS.length} patterns`}
          </span>
        </button>

        {showPresets && (
          <div className="mt-2 space-y-1.5">
            {TIMING_PRESETS.map((preset) => (
              <button
                key={preset.id}
                type="button"
                onClick={() => handleApplyPreset(preset)}
                className="w-full flex items-start gap-3 px-3 py-2.5 rounded-lg border border-border-subtle bg-dark-elevated-1 text-left transition-all hover:bg-dark-elevated-2 hover:border-cyan-500/30 group"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-text-primary group-hover:text-cyan-300 transition-colors">
                      {preset.label}
                    </span>
                    <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-dark-elevated-2 text-text-muted">
                      {preset.sport}
                    </span>
                  </div>
                  <p className="text-[11px] text-text-muted mt-0.5 line-clamp-1">
                    {preset.description}
                  </p>
                </div>
                <ArrowsRightLeftIcon className="w-4 h-4 text-text-muted flex-shrink-0 mt-0.5 group-hover:text-cyan-400 transition-colors" />
              </button>
            ))}
          </div>
        )}
      </div>

      {/* ── Sentence Builder ──────────────────────────────────────────────── */}
      {/* Primary config form: five inline slot-pickers that build the timing  */}
      {/* rule as a plain-language sentence. The underlying TimingConfig JSON  */}
      {/* is identical to what the Advanced event cards produce — this is a    */}
      {/* UI-representation layer only. The Advanced section below provides    */}
      {/* angle-threshold details, timing constraints, and template reference. */}
      <div
        data-testid="timing-sentence-summary"
        className="mb-3 px-3 py-2.5 rounded-lg bg-cyan-500/8 border border-cyan-500/20"
      >
        <SentenceBuilder
          config={config}
          updateConfig={updateConfig}
          eventA={eventA}
          eventB={eventB}
        />
      </div>

      {/* ── Advanced (collapsible) ────────────────────────────────────────── */}
      {/* Event cards, relationship, timing constraints, and template          */}
      {/* reference live here. Default-collapsed so the sentence summary       */}
      {/* is the primary view after selecting a preset.                        */}
      <div className="mb-1">
        <button
          type="button"
          aria-expanded={showAdvanced}
          onClick={() => setShowAdvanced(!showAdvanced)}
          className="w-full flex items-center justify-between px-3 py-2 rounded-lg bg-dark-elevated-1 border border-border-subtle hover:border-border-strong transition-colors"
        >
          <span className="text-xs font-semibold text-text-secondary uppercase tracking-wider">
            Advanced
          </span>
          <ChevronDownIcon
            className={cn(
              'w-3.5 h-3.5 text-text-muted transition-transform duration-200',
              showAdvanced && 'rotate-180',
            )}
          />
        </button>
      </div>

      {showAdvanced && (
        <div className="space-y-0">
          {/* ── Event Card A ────────────────────────────────────────────── */}
          <EventCard
            side="A"
            landmark={config.landmark_a}
            eventConfig={eventA}
            onSelectLandmark={handleSelectLandmarkA}
            onLandmarkChange={(name) => updateConfig({ landmark_a: name })}
            onEventConfigChange={handleEventAChange}
            canSelectLandmark={!!onRequestLandmarkASelection}
          />

          {/* ── Relationship Connector ──────────────────────────────────── */}
          <RelationshipConnector
            relationship={config.relationship}
            onChange={(rel) => updateConfig({ relationship: rel })}
          />

          {/* ── Event Card B ────────────────────────────────────────────── */}
          <EventCard
            side="B"
            landmark={config.landmark_b}
            eventConfig={eventB}
            onSelectLandmark={handleSelectLandmarkB}
            onLandmarkChange={(name) => updateConfig({ landmark_b: name })}
            onEventConfigChange={handleEventBChange}
            canSelectLandmark={!!onRequestLandmarkBSelection}
          />

          {/* ── Timing Constraints ────────────────────────────────────────── */}
          <div className="mt-4 space-y-3">
            <div className="h-px bg-gradient-to-r from-transparent via-border-subtle to-transparent" />

            {config.relationship === TimingRelationship.SIMULTANEOUS ? (
              /* Simultaneity tolerance */
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-[11px] font-semibold text-text-secondary uppercase tracking-wider">
                    Tolerance Window
                  </label>
                  <span className="text-xs font-semibold text-cyan-400 tabular-nums">
                    ±{config.simultaneity_tolerance_frames} frames
                  </span>
                </div>
                <input
                  type="range"
                  min="1"
                  max="15"
                  step="1"
                  value={config.simultaneity_tolerance_frames}
                  onChange={(e) =>
                    updateConfig({ simultaneity_tolerance_frames: parseInt(e.target.value) })
                  }
                  className="w-full h-1.5 bg-dark-elevated-2 rounded-lg appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-cyan-500 [&::-webkit-slider-thumb]:cursor-pointer [&::-webkit-slider-thumb]:shadow-md"
                />
                <div className="flex justify-between text-[10px] text-text-muted mt-0.5">
                  <span>Tight (1)</span>
                  <span>Relaxed (15)</span>
                </div>
              </div>
            ) : (
              /* Sequential lag range */
              <div className="space-y-3">
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <label className="text-[11px] font-semibold text-text-secondary uppercase tracking-wider">
                      Minimum Gap
                    </label>
                    <span className="text-xs font-semibold text-cyan-400 tabular-nums">
                      {config.min_lag_frames ?? 0} frames
                    </span>
                  </div>
                  <input
                    type="range"
                    min="0"
                    max="30"
                    step="1"
                    value={config.min_lag_frames ?? 0}
                    onChange={(e) => updateConfig({ min_lag_frames: parseInt(e.target.value) })}
                    className="w-full h-1.5 bg-dark-elevated-2 rounded-lg appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-cyan-500 [&::-webkit-slider-thumb]:cursor-pointer [&::-webkit-slider-thumb]:shadow-md"
                  />
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <label className="text-[11px] font-semibold text-text-secondary uppercase tracking-wider">
                      Maximum Gap
                    </label>
                    <span className="text-xs font-semibold text-cyan-400 tabular-nums">
                      {config.max_lag_frames ?? 30} frames
                    </span>
                  </div>
                  <input
                    type="range"
                    min="1"
                    max="60"
                    step="1"
                    value={config.max_lag_frames ?? 30}
                    onChange={(e) => updateConfig({ max_lag_frames: parseInt(e.target.value) })}
                    className="w-full h-1.5 bg-dark-elevated-2 rounded-lg appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-cyan-500 [&::-webkit-slider-thumb]:cursor-pointer [&::-webkit-slider-thumb]:shadow-md"
                  />
                </div>
                <p className="text-[11px] text-text-muted">
                  How far apart (in frames) the two events can be
                </p>
              </div>
            )}
          </div>

          {/* ── Template Reference ──────────────────────────────────────────── */}
          <div className="mt-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={config.use_template_reference}
                onChange={(e) => updateConfig({ use_template_reference: e.target.checked })}
                className="w-4 h-4 rounded border-border-strong text-cyan-500 focus:ring-cyan-500"
              />
              <span className="text-sm font-medium text-text-primary">
                Compare to template timing
              </span>
            </label>
            {config.use_template_reference && (
              <div className="mt-2.5 ml-6">
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-[11px] font-semibold text-text-secondary uppercase tracking-wider">
                    Tolerance
                  </label>
                  <span className="text-xs font-semibold text-cyan-400">
                    ±{config.template_tolerance_percent}%
                  </span>
                </div>
                <input
                  type="range"
                  min="10"
                  max="50"
                  step="5"
                  value={config.template_tolerance_percent}
                  onChange={(e) =>
                    updateConfig({ template_tolerance_percent: parseFloat(e.target.value) })
                  }
                  className="w-full h-1.5 bg-dark-elevated-2 rounded-lg appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-cyan-500 [&::-webkit-slider-thumb]:cursor-pointer [&::-webkit-slider-thumb]:shadow-md"
                />
                <div className="flex justify-between text-[10px] text-text-muted mt-0.5">
                  <span>Strict (10%)</span>
                  <span>Lenient (50%)</span>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Phase Context ───────────────────────────────────────────────── */}
      {currentPhase && (
        <div className="mt-4 px-3 py-2 bg-info/10 border border-info/30 rounded-lg text-xs text-info">
          <span className="font-medium">Phase:</span> {currentPhase.name}
          <span className="text-info/70 ml-2">
            (frames {currentPhase.start_frame}–{currentPhase.end_frame})
          </span>
        </div>
      )}

      {/* ── No-Phase Warning ────────────────────────────────────────────── */}
      {showPhaseWarning && (
        <div className="mt-4 px-3 py-2.5 rounded-lg bg-amber-400/10 border border-amber-400/20 text-xs text-amber-400 leading-relaxed">
          <span className="font-semibold">No phase selected</span> — this timing goal will evaluate
          across the entire video. For best results, assign it to the phase where the timed movement
          occurs.
        </div>
      )}

      {/* ── Status ──────────────────────────────────────────────────────── */}
      {hasCompleteTimingGoal && (
        <div className="mt-3 p-3 bg-info/10 border border-info/30 rounded-lg">
          <div className="flex items-center gap-2 text-sm text-info">
            <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                clipRule="evenodd"
              />
            </svg>
            <span className="font-medium">Ready to save</span>
          </div>
          <p className="text-xs text-info/80 mt-1 ml-7">
            {getLandmarkLabel(config.landmark_a)}{' '}
            {config.relationship === TimingRelationship.SIMULTANEOUS ? '↔' : '→'}{' '}
            {getLandmarkLabel(config.landmark_b)}
          </p>
        </div>
      )}

      {!config.landmark_a && !config.landmark_b && !showPresets && (
        <div className="mt-3 p-3 bg-dark-elevated-1 border border-border-subtle rounded-lg text-sm text-text-secondary text-center">
          Select a <strong>Quick Preset</strong> above, or pick two body parts on the skeleton
        </div>
      )}
      </div>
    </AnnotationPanel>
  );
}
